/**
 * Create Replenishment Module ("Opprett påfylling")
 *
 * Manual trigger for the replenishment rule group — the Compass counterpart of the
 * legacy ribbon button "Opprett påfylling". Runs the rule group configured in
 * SystemSetup.StdReplenishmentRuleGroupID, which scans pick bins below their
 * replenish minimum level and creates a replenishment order (StcIntRelOrder,
 * TransType=2) with reservations. Operators execute the order in the Utlegg module.
 */

import React, { useState } from 'react';
import { ArrowDownToLine, Loader2, CheckCircle2, AlertTriangle, Info, PackageCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { replenishmentService, RunReplenishmentResponse } from '../../../../services/replenishmentService';

interface CreateReplenishmentAppProps {
  onBack?: () => void;
}

export const CreateReplenishmentApp: React.FC<CreateReplenishmentAppProps> = ({ onBack }) => {
  const { t } = useTranslation();

  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<RunReplenishmentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showHow, setShowHow] = useState(false);

  const runReplenishment = async () => {
    setIsRunning(true);
    setError(null);
    setResult(null);

    try {
      const response = await replenishmentService.runReplenishment();
      setResult(response);
      if (!response.success) {
        setError(response.errorMessage || t('manager.modules.createReplenishment.unknownError', {}, 'Ukjent feil'));
      }
    } catch (err: any) {
      setError(err.message || t('manager.modules.createReplenishment.unknownError', {}, 'Ukjent feil'));
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <BackButton onClick={onBack || (() => {})} />
          <ArrowDownToLine className="w-8 h-8 text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {t('manager.modules.createReplenishment.title', {}, 'Opprett påfylling')}
            </h1>
            <p className="text-sm text-gray-500">
              {t('manager.modules.createReplenishment.subtitle', {}, 'Flytt varer fra bufferlokasjon til plukklokasjon')}
            </p>
          </div>
        </div>

        {/* Explanation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                {t(
                  'manager.modules.createReplenishment.description1',
                  {},
                  'Funksjonen finner plukklokasjoner som er under sitt minimumsnivå for påfylling, og oppretter en påfyllingsordre som flytter varer fra buffer-/lagerlokasjoner til plukklokasjonene.'
                )}
              </p>
              <p>
                {t(
                  'manager.modules.createReplenishment.description2',
                  {},
                  'Ordren utføres av operatørene i Utlegg-modulen. Lokasjoner som allerede har en åpen påfyllingsordre blir ikke tatt med på nytt.'
                )}
              </p>

              {/* Expandable step-by-step explanation */}
              <button
                onClick={() => setShowHow(!showHow)}
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium mt-1"
              >
                {showHow ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {t('manager.modules.createReplenishment.how.title', {}, 'Hva skjer når du trykker?')}
              </button>

              {showHow && (
                <div className="mt-2 space-y-2 border-l-2 border-blue-200 pl-4">
                  <ol className="list-decimal list-inside space-y-2">
                    <li>
                      <span className="font-medium text-gray-700">
                        {t('manager.modules.createReplenishment.how.step1Title', {}, 'Finn behov.')}
                      </span>{' '}
                      {t(
                        'manager.modules.createReplenishment.how.step1',
                        {},
                        'Alle plukklokasjoner med automatisk påfylling sjekkes: har beholdningen falt under minimumsnivået? Lokasjoner med en åpen påfylling underveis hoppes over.'
                      )}
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">
                        {t('manager.modules.createReplenishment.how.step2Title', {}, 'Beregn mengde.')}
                      </span>{' '}
                      {t(
                        'manager.modules.createReplenishment.how.step2',
                        {},
                        'For hver lokasjon regnes ut hvor mye som skal fylles på — opp til maksnivået, eventuelt begrenset av maks-mengde per påfylling på varen.'
                      )}
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">
                        {t('manager.modules.createReplenishment.how.step3Title', {}, 'Finn kilde.')}
                      </span>{' '}
                      {t(
                        'manager.modules.createReplenishment.how.step3',
                        {},
                        'For hver vare velges bufferlokasjoner som har varen tilgjengelig, etter regelverket for kildevalg.'
                      )}
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">
                        {t('manager.modules.createReplenishment.how.step4Title', {}, 'Reservér og opprett ordre.')}
                      </span>{' '}
                      {t(
                        'manager.modules.createReplenishment.how.step4',
                        {},
                        'Varene reserveres på kildelokasjonene, og det opprettes én påfyllingsordre med én linje per flytt: hent X stk fra lokasjon A, legg på plukklokasjon B. Alt skjer i én transaksjon — feiler noe, rulles alt tilbake.'
                      )}
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">
                        {t('manager.modules.createReplenishment.how.step5Title', {}, 'Svar.')}
                      </span>{' '}
                      {t(
                        'manager.modules.createReplenishment.how.step5',
                        {},
                        'Skjermen viser ordrenummer, antall lokasjoner med behov og antall linjer. Var ingen under minimum, opprettes ingenting.'
                      )}
                    </li>
                  </ol>
                  <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                    {t(
                      'manager.modules.createReplenishment.how.important',
                      {},
                      'Viktig: Knappen flytter ingen varer — den lager bare arbeidsordren. Selve flyttingen gjør operatøren i Utlegg-modulen, og først da oppdateres lagersaldoen.'
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Run button */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 flex flex-col items-center gap-4">
          <button
            onClick={runReplenishment}
            disabled={isRunning}
            className="flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
          >
            {isRunning ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('manager.modules.createReplenishment.running', {}, 'Oppretter påfylling...')}
              </>
            ) : (
              <>
                <ArrowDownToLine className="w-5 h-5" />
                {t('manager.modules.createReplenishment.runButton', {}, 'Opprett påfylling')}
              </>
            )}
          </button>
          <p className="text-xs text-gray-400">
            {t(
              'manager.modules.createReplenishment.runHint',
              {},
              'Kjøringen kan trygt gjentas — den oppretter kun det som mangler.'
            )}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">
                {t('manager.modules.createReplenishment.errorTitle', {}, 'Kunne ikke opprette påfylling')}
              </p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Result */}
        {result && result.success && (
          <div
            className={`rounded-lg border p-4 sm:p-6 ${
              result.replenishmentNeeded
                ? 'bg-green-50 border-green-200'
                : 'bg-blue-50 border-blue-200'
            }`}
          >
            {result.replenishmentNeeded ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  <h2 className="text-lg font-semibold text-green-800">
                    {t('manager.modules.createReplenishment.successTitle', {}, 'Påfyllingsordre opprettet')}
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div className="bg-white rounded-md border border-green-200 p-3">
                    <p className="text-gray-500">
                      {t('manager.modules.createReplenishment.orderNo', {}, 'Ordrenummer')}
                    </p>
                    <p className="text-xl font-bold text-gray-800">#{result.stcOrderNo}</p>
                  </div>
                  <div className="bg-white rounded-md border border-green-200 p-3">
                    <p className="text-gray-500">
                      {t('manager.modules.createReplenishment.processedCount', {}, 'Lokasjoner med behov')}
                    </p>
                    <p className="text-xl font-bold text-gray-800">{result.processedCount}</p>
                  </div>
                  <div className="bg-white rounded-md border border-green-200 p-3">
                    <p className="text-gray-500">
                      {t('manager.modules.createReplenishment.lineCount', {}, 'Ordrelinjer')}
                    </p>
                    <p className="text-xl font-bold text-gray-800">{result.totalLineCount}</p>
                  </div>
                </div>
                {result.message && (
                  <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    {result.message}
                  </p>
                )}
                <p className="text-sm text-green-700 flex items-center gap-2">
                  <PackageCheck className="w-4 h-4" />
                  {t(
                    'manager.modules.createReplenishment.nextStep',
                    {},
                    'Ordren er klar for utførelse i Utlegg-modulen.'
                  )}
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <div>
                  <h2 className="text-lg font-semibold text-blue-800">
                    {t('manager.modules.createReplenishment.noNeedTitle', {}, 'Ingen påfylling nødvendig')}
                  </h2>
                  <p className="text-sm text-blue-700 mt-1">
                    {result.message ||
                      t(
                        'manager.modules.createReplenishment.noNeedText',
                        {},
                        'Ingen plukklokasjoner er under minimumsnivået sitt.'
                      )}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
